import last from 'lodash/last';
import { lookup } from 'mime-types';
import { writeText } from 'clipboard-polyfill';

import { FileUploadParams, UploadProcess } from './types';

import { axios } from '@lib/axios';

import { PacsApi } from '@api/PacsApi';

import { Validation } from '@utils/Validation';
import { assertIsDefined } from '@utils/assertIsDefined';

import { S3Service, UploadedPart } from './S3Service';
import { EntityFileData, FileEntityType } from 'chaika-frontend-types/frontend-cms';

const MEDIALOG_FILE_PATH = 'https://chaika.com';

const BASE_URL = '/api/file';

export class FileApi {
    public static async downloadFile(path: string, downloadFileName?: string): Promise<void> {
        const fileService = this.getFileService();
        const downloadLink =
            Validation.isUrl(path) || path.startsWith(MEDIALOG_FILE_PATH)
                ? path
                : await fileService.getDownloadLink(path, false, downloadFileName);
        window.open(downloadLink, '_blank');
    }

    public static async sharePacs(path: string): Promise<void> {
        const url = await PacsApi.getShareLink(path);

        writeText(url);
        window.open(url, '_blank');
    }

    // downloadFileName is only honored for own (S3) files via Content-Disposition on the signed URL;
    // for external URLs and MEDIALOG paths the browser uses the upstream filename.
    public static async showFile(path: string, downloadFileName?: string): Promise<void> {
        if (Validation.isUrl(path) || path.startsWith(MEDIALOG_FILE_PATH)) {
            window.open(path, '_blank');
        } else {
            await this.showOwnFile(path, downloadFileName);
        }
    }

    public static async makeSharedLink(path: string): Promise<string> {
        const fileService = this.getFileService();
        return fileService.makeSharedLink(path);
    }

    public static async getSharedLink(path: string, downloadFileName?: string): Promise<string> {
        const fileService = this.getFileService();
        return fileService.getSharedLink(path, downloadFileName);
    }

    public static async upload(file: File, params: FileUploadParams): Promise<void> {
        const fileService = this.getFileService();
        await fileService.upload(file, params);
    }

    public static uploadProcess(file: File, params: FileUploadParams): AsyncIterableIterator<UploadProcess> {
        const fileService = this.getFileService();
        return fileService.uploadProcess(file, params);
    }

    public static async initMultipartUpload(params: FileUploadParams): Promise<string> {
        return this.getFileService().initMultipartUpload(params);
    }

    public static async uploadMultipartPart(
        params: FileUploadParams,
        chunk: Blob,
        uploadId: string,
        partNumber: number,
    ): Promise<UploadedPart> {
        return this.getFileService().uploadMultipartPart(params, chunk, uploadId, partNumber);
    }

    public static async completeMultipartUpload(
        params: FileUploadParams,
        uploadId: string,
        uploadedParts: UploadedPart[],
    ): Promise<void> {
        return this.getFileService().completeMultipartUpload(params, uploadId, uploadedParts);
    }

    public static async getFiles(entityIds: string[], entityType: FileEntityType): Promise<EntityFileData[]> {
        const req = await axios.get(`${BASE_URL}/`, { params: { entityIds: entityIds.join(','), entityType } });
        return req.data.files;
    }

    public static async addEntityFile(entityId: string, entityType: FileEntityType, fileName: string): Promise<void> {
        await axios.post(`/api/${entityType}/${entityId}/file`, { fileName });
        await axios.put(`/api/${entityType}/${entityId}/file/${fileName}/status/uploaded`);
    }

    public static async addPacsFile(entityId: string, entityType: FileEntityType, fileName: string): Promise<void> {
        await axios.post(`/api/${entityType}/${entityId}/file/pacs`, { fileName });
    }

    public static async deleteEntityFile(
        entityId: string,
        entityType: FileEntityType,
        fileName: string,
    ): Promise<void> {
        await axios.delete(`/api/${entityType}/${entityId}/file/${fileName}`);
    }

    protected static async showOwnFile(path: string, downloadFileName?: string): Promise<void> {
        const fileService = this.getFileService();
        const downloadLink = await fileService.getDownloadLink(path, true, downloadFileName);

        const fileName = last(path.split('/'));
        assertIsDefined(fileName, 'Splitting a file path always yields at least one segment');
        const mimeType = lookup(fileName) || 'application/force-download';

        if (mimeType.startsWith('image')) {
            const newWindow = window.open('about:blank', '_blank');
            if (newWindow) {
                newWindow.document.head.innerHTML = `<title>${fileName}</title>`;
                newWindow.document.body.innerHTML = `<embed src="${downloadLink}" type="${mimeType}"></embed>`;
            }
        } else if (downloadFileName) {
            window.open(downloadLink, '_blank');
        } else {
            fetch(downloadLink)
                .then(res => res.blob())
                .then(blob => {
                    const pdfBlob = new Blob([blob], { type: mimeType });
                    const blobUrl = URL.createObjectURL(pdfBlob);
                    window.open(blobUrl, '_blank');
                });
        }
    }

    protected static getFileService(): S3Service {
        return new S3Service();
    }
}
